import { matchIsValidTel } from 'mui-tel-input';
import { type FC, useState } from 'react';

import { ZERO } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { type User, type UserNotifications } from '@/types';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  Typography,
} from '@/ui';

import Details from './Details';
import Notifications from './Notifications';

import ToggleCard from '@/components/Profile/Card/Toggle';
import {
  useUpdateUserNotificationsMutation,
  useUpdateUserProfileMutation,
  useUpdateUserTranscriptSharingMutation,
} from '@/store/api/userApi';

interface EditClientAccountProps {
  client: User;
  onClose: (updated?: Partial<User>) => void;
}

const EditClientAccount: FC<EditClientAccountProps> = ({ client, onClose }) => {
  const { refetchUser } = useCurrentUser();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [updateUserNotifications] = useUpdateUserNotificationsMutation();
  const [updateUserTranscriptSharing] =
    useUpdateUserTranscriptSharingMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [timezone, setTimezone] = useState(client.profile?.timezone || '');
  const [phoneValue, setPhoneValue] = useState(client.profile?.phone || '');
  const [phoneE164, setPhoneE164] = useState(client.profile?.phone || '');
  const [phoneNationalNumber, setPhoneNationalNumber] = useState('');
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [preferences, setPreferences] = useState(
    client.userNotifications ?? {},
  );
  const [autoShareSessionTranscripts, setAutoShareSessionTranscripts] =
    useState(!!client.autoShareSessionTranscripts);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const onPhoneChange = (
    value: string,
    e164: string,
    nationalNumber: string,
    reason: 'country' | 'input',
  ) => {
    if (reason === 'input' || nationalNumber.trim().length > ZERO) {
      setPhoneTouched(true);
    }
    setPhoneValue(value);
    setPhoneE164(e164);
    setPhoneNationalNumber(nationalNumber);
  };
  const onTimezoneChange = (value: string) => setTimezone(value);
  const onPreferencesChange = (value: UserNotifications) =>
    setPreferences(value);

  const hasPhone =
    phoneNationalNumber.trim().length > ZERO ||
    (!phoneTouched && phoneValue.trim().length > ZERO);
  const isPhoneInvalid =
    hasPhone &&
    (!matchIsValidTel(phoneValue) ||
      (phoneTouched && !phoneE164.startsWith('+')));
  const phoneHelperText = isPhoneInvalid
    ? 'Phone is invalid'
    : '*Required to use SMS notifications';

  const onSave = async () => {
    setIsSaving(true);
    setErrorMessage(null);
    try {
      await updateUserProfile({
        id: client.id,
        userProfile: {
          timezone,
          ...(phoneTouched ? { phone: hasPhone ? phoneE164 : '' } : {}),
        },
      }).unwrap();

      await updateUserNotifications({
        id: client.id,
        notifications: preferences,
      }).unwrap();

      try {
        await updateUserTranscriptSharing({
          enabled: autoShareSessionTranscripts,
          id: client.id,
        }).unwrap();
      } catch {
        await refetchUser();
        setErrorMessage(
          'Saved changes, but could not update transcript sharing. Please try again.',
        );
        return;
      }

      await refetchUser();
      onClose();
    } catch {
      setErrorMessage('Could not save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };
  const onCancel = () => onClose();

  const disableSave = phoneTouched && isPhoneInvalid;

  return (
    <Dialog maxWidth="md" fullWidth open>
      <DialogContent>
        {errorMessage && (
          <Alert className="mb-4" severity="error">
            {errorMessage}
          </Alert>
        )}
        <Details
          email={client.email}
          phone={phoneValue}
          phoneHelperText={phoneHelperText}
          phoneInvalid={isPhoneInvalid}
          timezone={timezone}
          onPhoneChange={onPhoneChange}
          onTimezoneChange={onTimezoneChange}
        />
        <Divider className="my-8" />
        <Notifications
          preferences={preferences}
          onPreferencesChange={onPreferencesChange}
        />
        <Divider className="my-8" />
        <Grid spacing={4} container>
          <Grid md={5} xs={12} item>
            <Typography className="mb-2" variant="h4">
              Transcripts
            </Typography>
          </Grid>
          <Grid className="mt-1 flex flex-col gap-3" md={7} xs={12} item>
            <ToggleCard
              icon="note"
              isSelected={autoShareSessionTranscripts}
              title="Share session transcripts with me"
              onClick={() => setAutoShareSessionTranscripts((prev) => !prev)}
            />
            <Typography className="text-cement-400" variant="body2">
              {' '}
              New coaching session transcripts will be shared in Google Docs
              with {client.documentsEmail ?? client.email}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          color="primary"
          disabled={disableSave}
          loading={isSaving}
          variant="contained"
          onClick={onSave}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditClientAccount;
